import { useEffect, useState } from 'react';
import type { ApiClient } from '../../api';
import type {
  MsrAksAufbauZeile, MsrAksProtokollZeile, MsrSeite, MsrVdiAggregatkatalogZeile,
} from './typen';
import { AKS_KUERZEL, z } from './gemeinsam';
import { zeitpunkt } from './kopf';
import {
  ConfirmDialog, ContentSection, DataTable, EmptyState, ErrorState,
  type Spalte,
} from '../../muster';

const GROESSE = 50;

/** Beispielwerte für das zusammengesetzte AKS-Beispiel — nur zur Anschauung. */
const BEISPIEL_WERTE: Record<string, string> = {
  Teilprojekt: 'TP1', Gewerk: 'RLT', Anlage: 'BOW.6OG.1',
  Aggregat: 'ZU-VENT01', Funktion: 'BM', Laufnummer: '',
};

/** Das literale Beispiel aus der Vorgabe Teuber + Viel, Abschnitt 13.2. */
const BAS_BEISPIEL = '8000_HG_O6_02_ASP07_LUE_ANL01-KZU01_BM';

/**
 * Reiter „AKS" — Benutzeradresse (BAS, Vorgabe Teuber + Viel Abschnitt 13.2),
 * Aufbau (bearbeitbar), Beispiel, Kürzelkatalog (statisch, ergänzt um die
 * dreistelligen Aggregatkürzel), Vergabeprotokoll (seitenweise).
 */
export function Aks({ api, projektId, aufbau, aggregatkatalog, onAufbauGespeichert }: {
  api: ApiClient; projektId: string; aufbau: MsrAksAufbauZeile[];
  aggregatkatalog: MsrVdiAggregatkatalogZeile[];
  onAufbauGespeichert: (neu: MsrAksAufbauZeile[]) => void;
}) {
  const [zeilen, setZeilen] = useState<MsrAksAufbauZeile[]>(aufbau);
  const [speichertLaeuft, setSpeichertLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [protokollSeite, setProtokollSeite] = useState(0);
  const [protokoll, setProtokoll] = useState<MsrSeite<MsrAksProtokollZeile> | null>(null);
  // TM-8c: Der Abruf des Protokolls hat einen eigenen Fehlerzustand - vorher stand ein Abruffehler als
  // „Der Aufbau konnte nicht gespeichert werden.“ und die Tabelle als „Noch keine Kennung vergeben.“.
  const [protokollFehler, setProtokollFehler] = useState<string | null>(null);
  const [protokollVersuch, setProtokollVersuch] = useState(0);
  const [speichernFrage, setSpeichernFrage] = useState(false);

  useEffect(() => setZeilen(aufbau), [aufbau]);

  useEffect(() => {
    setProtokoll(null);
    setProtokollFehler(null);
    api.msrAksProtokoll(projektId, { seite: protokollSeite, groesse: GROESSE })
      .then(setProtokoll)
      .catch((e) => setProtokollFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId, protokollSeite, protokollVersuch]);

  const feldAendern = (i: number, teil: Partial<MsrAksAufbauZeile>) =>
    setZeilen((z2) => z2.map((r, j) => (j === i ? { ...r, ...teil } : r)));

  const zeileVerschieben = (i: number, richtung: -1 | 1) => setZeilen((z2) => {
    const j = i + richtung;
    if (j < 0 || j >= z2.length) return z2;
    const neu = z2.slice();
    [neu[i], neu[j]] = [neu[j], neu[i]];
    return neu.map((r, k) => ({ ...r, position: k + 1 }));
  });

  const zeileHinzufuegen = () => setZeilen((z2) => [...z2, {
    id: `neu-${Date.now()}`, position: z2.length + 1, feld: '', quelle: '',
    trennzeichen: '.', istAktiv: true,
  }]);

  const zeileEntfernen = (i: number) => setZeilen((z2) =>
    z2.filter((_, j) => j !== i).map((r, k) => ({ ...r, position: k + 1 })));

  const speichern = async () => {
    setSpeichernFrage(false);
    setSpeichertLaeuft(true);
    setFehler(null);
    try {
      const antwort = await api.msrAksAufbauSetzen(projektId, zeilen);
      onAufbauGespeichert(antwort);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichertLaeuft(false);
    }
  };

  const beispiel = zeilen
    .filter((r) => r.istAktiv)
    .slice().sort((a, b) => a.position - b.position)
    .map((r) => BEISPIEL_WERTE[r.feld] ?? `[${r.feld || '?'}]`)
    .filter((v) => v !== '')
    .join(zeilen[0]?.trennzeichen || '.');

  const positionen = aufbau
    .filter((r) => r.istAktiv)
    .slice().sort((a, b) => a.position - b.position);

  const positionSpalten: Spalte<MsrAksAufbauZeile>[] = [
    { schluessel: 'position', titel: 'Position', art: 'zahl', breite: '90px',
      wert: (r) => r.position },
    { schluessel: 'feld', titel: 'Feld', wert: (r) => r.feld, immerSichtbar: true },
    { schluessel: 'quelle', titel: 'Quelle', wert: (r) => r.quelle },
  ];

  const aufbauSpalten: Spalte<MsrAksAufbauZeile>[] = [
    { schluessel: 'position', titel: 'Position', art: 'zahl', breite: '80px',
      wert: (r) => r.position },
    {
      schluessel: 'feld', titel: 'Feld', immerSichtbar: true, sortierbar: false,
      wert: (r) => r.feld,
      inhalt: (r) => (
        <input type="text" value={r.feld} style={{ width: 110 }}
          aria-label={`Feld der Position ${r.position}`}
          onChange={(e) => feldAendern(zeilen.indexOf(r), { feld: e.target.value })} />
      ),
    },
    {
      schluessel: 'quelle', titel: 'Quelle', sortierbar: false, wert: (r) => r.quelle,
      inhalt: (r) => (
        <input type="text" value={r.quelle} style={{ width: 170 }}
          aria-label={`Quelle der Position ${r.position}`}
          onChange={(e) => feldAendern(zeilen.indexOf(r), { quelle: e.target.value })} />
      ),
    },
    {
      schluessel: 'laenge', titel: 'Länge', breite: '90px', sortierbar: false,
      wert: (r) => r.laenge ?? null,
      inhalt: (r) => (
        <input type="number" min={0} value={r.laenge ?? ''} style={{ width: 60 }}
          aria-label={`Länge der Position ${r.position}`}
          onChange={(e) => feldAendern(zeilen.indexOf(r), {
            laenge: e.target.value ? Number(e.target.value) : undefined,
          })} />
      ),
    },
    {
      schluessel: 'trennzeichen', titel: 'Trennz.', breite: '80px', sortierbar: false,
      wert: (r) => r.trennzeichen,
      inhalt: (r) => (
        <input type="text" value={r.trennzeichen} style={{ width: 44 }}
          aria-label={`Trennzeichen der Position ${r.position}`}
          onChange={(e) => feldAendern(zeilen.indexOf(r), { trennzeichen: e.target.value })} />
      ),
    },
    {
      schluessel: 'begruendung', titel: 'Begründung', sortierbar: false,
      wert: (r) => r.begruendung ?? null,
      inhalt: (r) => (
        <input type="text" value={r.begruendung ?? ''} style={{ width: '100%' }}
          aria-label={`Begründung der Position ${r.position}`}
          onChange={(e) => feldAendern(zeilen.indexOf(r), { begruendung: e.target.value })} />
      ),
    },
    {
      schluessel: 'istAktiv', titel: 'aktiv', breite: '70px', ausrichtung: 'mitte',
      sortierbar: false, wert: (r) => (r.istAktiv ? 'ja' : 'nein'),
      inhalt: (r) => (
        <input type="checkbox" checked={r.istAktiv}
          aria-label={`Position ${r.position} aktiv`}
          onChange={(e) => feldAendern(zeilen.indexOf(r), { istAktiv: e.target.checked })} />
      ),
    },
    {
      schluessel: 'aktionen', titel: 'Reihenfolge', breite: '120px', sortierbar: false,
      wert: () => '',
      inhalt: (r) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <button type="button" className="m-knopf m-knopf-nur-zeichen"
            aria-label={`Position ${r.position} nach oben`}
            onClick={() => zeileVerschieben(zeilen.indexOf(r), -1)}>↑</button>
          <button type="button" className="m-knopf m-knopf-nur-zeichen"
            aria-label={`Position ${r.position} nach unten`}
            onClick={() => zeileVerschieben(zeilen.indexOf(r), 1)}>↓</button>
          <button type="button" className="m-knopf m-knopf-nur-zeichen"
            aria-label={`Position ${r.position} entfernen`}
            onClick={() => zeileEntfernen(zeilen.indexOf(r))}>✕</button>
        </span>
      ),
    },
  ];

  const kuerzelSpalten: Spalte<{ kuerzel: string; bedeutung: string }>[] = [
    {
      schluessel: 'kuerzel', titel: 'Kürzel', breite: '110px', immerSichtbar: true,
      wert: (k) => k.kuerzel,
      inhalt: (k) => <span style={{ fontFamily: 'monospace' }}>{k.kuerzel}</span>,
    },
    { schluessel: 'bedeutung', titel: 'Bedeutung', wert: (k) => k.bedeutung },
  ];

  const aggregatSpalten: Spalte<MsrVdiAggregatkatalogZeile>[] = [
    {
      schluessel: 'kuerzel', titel: 'Kürzel', breite: '110px', immerSichtbar: true,
      wert: (k) => k.kuerzel,
      inhalt: (k) => <span style={{ fontFamily: 'monospace' }}>{k.kuerzel}</span>,
    },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (k) => k.bezeichnung },
    { schluessel: 'gewerke', titel: 'Gewerke', wert: (k) => k.gewerke.join(', ') },
    { schluessel: 'quelleVorlage', titel: 'aus Vorlage', breite: '110px',
      wert: (k) => (k.quelleVorlage ? 'ja' : 'nein') },
  ];

  const protokollSpalten: Spalte<MsrAksProtokollZeile>[] = [
    {
      schluessel: 'schluessel', titel: 'Schlüssel', immerSichtbar: true,
      wert: (pz) => pz.schluessel,
      inhalt: (pz) => (
        <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{pz.schluessel}</span>
      ),
    },
    {
      schluessel: 'vorherSchluessel', titel: 'vorher', wert: (pz) => pz.vorherSchluessel ?? null,
      inhalt: (pz) => (pz.vorherSchluessel
        ? <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{pz.vorherSchluessel}</span>
        : '—'),
    },
    { schluessel: 'zielId', titel: 'Ziel', wert: (pz) => pz.zielId },
    { schluessel: 'vergebenVon', titel: 'Vergeben von', wert: (pz) => pz.vergebenVon },
    { schluessel: 'vergebenAm', titel: 'Vergeben am', art: 'datum',
      wert: (pz) => pz.vergebenAm,
      inhalt: (pz) => zeitpunkt(pz.vergebenAm) },
    { schluessel: 'grund', titel: 'Grund', wert: (pz) => pz.grund ?? null },
  ];

  return (
    <>
      <ContentSection
        titel="Benutzeradresse (BAS)"
        beschreibung={'Ersetzt den bisherigen AKS-Aufbau (Vorgabe Teuber + Viel, '
          + 'Abschnitt 13.2): <Projektnr>_<Gebäude>_<Geschoss>_<Bereich>_<ASP>_<Gewerk>_'
          + '<Anlage>-<Aggregat>. Der Datenpunkt-AKS ist BAS + „_" + Funktionskürzel '
          + '(z. B. BM, SM, FR — Kürzelkatalog unten).'}
      >
        <div style={{ fontFamily: 'monospace', fontSize: 15, marginBottom: 10 }}>
          {BAS_BEISPIEL}
        </div>

        <DataTable
          name="Aktiver Aufbau"
          spalten={positionSpalten}
          zeilen={positionen}
          schluessel={(r) => r.id}
          dicht
          suche={false}
          spaltenwahl={false}
          leer={(
            <EmptyState
              titel="Kein aktiver Aufbau."
              erklaerung={'Ohne aktive Position entsteht keine Benutzeradresse — unten '
                + 'mindestens eine Position anlegen und aktiv setzen.'}
            />
          )}
        />
      </ContentSection>

      <ContentSection
        titel="Aufbau"
        beschreibung="Reihenfolge und Quelle je Stelle der Benutzeradresse."
        aktionen={(
          <>
            <button type="button" className="m-knopf" onClick={zeileHinzufuegen}>
              Position hinzufügen
            </button>
            <button type="button" className="m-knopf m-knopf-haupt" disabled={speichertLaeuft}
              onClick={() => setSpeichernFrage(true)}>
              {speichertLaeuft ? 'speichert …' : 'Speichern'}
            </button>
          </>
        )}
      >
        <DataTable
          name="Positionen"
          spalten={aufbauSpalten}
          zeilen={zeilen}
          schluessel={(r) => r.id}
          dicht
          suche={false}
          spaltenwahl={false}
          leer={(
            <EmptyState
              titel="Der Aufbau hat noch keine Position."
              erklaerung="Ohne Position gibt es keine Benutzeradresse."
              aktion={(
                <button type="button" className="m-knopf" onClick={zeileHinzufuegen}>
                  Position hinzufügen
                </button>
              )}
            />
          )}
        />

        {fehler && (
          <ErrorState
            meldung="Der Aufbau konnte nicht gespeichert werden."
            technisch={fehler}
            beiErneutVersuchen={() => setSpeichernFrage(true)}
          />
        )}
      </ContentSection>

      <ConfirmDialog
        offen={speichernFrage}
        art="warnung"
        titel="Aufbau der Benutzeradresse speichern?"
        text={'Wirkt nur auf noch nicht vergebene Kennungen. Bereits vergebene '
          + 'Kennungen bleiben unverändert.'}
        bestaetigenText="Speichern"
        laeuft={speichertLaeuft}
        beiBestaetigen={() => void speichern()}
        beiAbbrechen={() => setSpeichernFrage(false)}
      />

      <ContentSection titel="Beispiel" beschreibung="Aus dem oben eingestellten Aufbau gebildet.">
        <div style={{ fontFamily: 'monospace', fontSize: 15 }}>
          {beispiel || <span className="schwach">— kein aktiver Aufbau —</span>}
        </div>
      </ContentSection>

      <ContentSection titel="Kürzelkatalog">
        <DataTable
          name="Funktionskürzel"
          spalten={kuerzelSpalten}
          zeilen={AKS_KUERZEL}
          schluessel={(k) => k.kuerzel}
          dicht
          ausfuhr
          ausfuhrDateiname="aks-funktionskuerzel"
          leer={<EmptyState klein titel="Der Kürzelkatalog ist leer." />}
        />

        <h4>Aggregatkürzel (dreistellig, BAS-Position „Aggregat", Abschnitt 13.3)</h4>

        <DataTable
          name="Aggregatkürzel"
          spalten={aggregatSpalten}
          zeilen={aggregatkatalog}
          schluessel={(k) => k.kuerzel}
          sortierung={{ spalte: 'kuerzel', richtung: 'auf' }}
          dicht
          ausfuhr
          ausfuhrDateiname="aks-aggregatkuerzel"
          leer={(
            <EmptyState
              titel="Kein Aggregatkürzel-Katalog geladen."
              erklaerung={'Der Katalog kommt aus den VDI-Grundlagen des Projekts — ohne '
                + 'ihn bleibt die BAS-Position „Aggregat" leer.'}
            />
          )}
        />
      </ContentSection>

      <ContentSection
        titel="Vergabeprotokoll"
        beschreibung="Jede vergebene Kennung mit Zeitpunkt und Grund — vergebene Kennungen
                      werden nie geändert."
      >
        {/* Seitenweise über den SERVER (50 je Seite): `proSeite={0}`, eigener
            `trefferText`. */}
        <DataTable
          name="Vergaben"
          spalten={protokollSpalten}
          zeilen={protokoll?.zeilen ?? []}
          schluessel={(pz) => pz.schluessel}
          trefferText={`${(protokoll?.zeilen ?? []).length} von `
            + `${z(protokoll?.gesamt ?? 0)} Vergaben`}
          laedt={protokoll === null && protokollFehler === null}
          fehler={protokollFehler ?? undefined}
          beiErneutVersuchen={() => setProtokollVersuch((v) => v + 1)}
          dicht
          proSeite={0}
          leer={(
            <EmptyState
              titel="Noch keine Kennung vergeben."
              erklaerung="Kennungen entstehen mit dem Ableitungslauf."
            />
          )}
        />

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <button type="button" className="m-knopf" disabled={protokollSeite <= 0}
            onClick={() => setProtokollSeite((s2) => s2 - 1)}>← Zurück</button>
          <span className="schwach" style={{ fontSize: 12 }}>
            Seite {protokollSeite + 1} ({z(protokoll?.gesamt ?? 0)} gesamt)
          </span>
          <button type="button" className="m-knopf"
            disabled={!protokoll || (protokollSeite + 1) * GROESSE >= (protokoll?.gesamt ?? 0)}
            onClick={() => setProtokollSeite((s2) => s2 + 1)}>Weiter →</button>
        </div>
      </ContentSection>
    </>
  );
}
